import React, { useEffect, useState } from 'react';
import { C } from '../constants';
import { currentClaims } from '../services/auth';
import { listarEquiposMonitoreo } from '../services/equiposFlux';
import { fluxaiEquipoUrl } from '../services/fluxai';
import ServiceIcon from './ServiceIcon';

function fila(label, value) {
  if (value == null || value === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, lineHeight: 1.5 }}>
      <span style={{ color: C.muted }}>{label}</span>
      <span style={{ color: C.heading, fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function Ficha({ eq }) {
  const href = fluxaiEquipoUrl(eq.device_id);
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.teal}`,
      borderRadius: 8, padding: 14, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: C.heading }}>
          {eq.alias || `Equipo ${eq.device_id}`}
        </div>
        <span style={{
          fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
          color: eq.en_linea ? '#00c781' : C.muted,
          background: eq.en_linea ? '#00c78118' : `${C.muted}14`,
          border: `1px solid ${eq.en_linea ? '#00c78144' : C.border}`,
          borderRadius: 5, padding: '3px 7px',
        }}>{eq.en_linea ? 'En línea' : (eq.estado || 'Ficha')}</span>
      </div>
      {fila('Identificador', eq.device_id)}
      {fila('Marca', eq.marca)}
      {fila('Modelo', eq.modelo)}
      {fila('Estado', eq.estado)}
      {fila('Protocolo', eq.protocolo)}
      {fila('Conexión', eq.tipo_conexion)}
      {fila('Medición', eq.tipo_medicion)}
      {fila('Última vez', eq.visto_en)}
      {eq.kwp != null && fila('Sistema', `${eq.kwp} kWp`)}
      <a
        href={href}
        target="alebas-fluxai"
        rel="noopener noreferrer"
        style={{
          marginTop: 4, alignSelf: 'flex-start', fontSize: 11.5, fontWeight: 700,
          color: C.teal, textDecoration: 'none', border: `1px solid ${C.teal}55`,
          borderRadius: 6, padding: '6px 12px',
        }}
      >
        Abrir este equipo en FluxAI ↗
      </a>
    </div>
  );
}

export default function Monitoreo({ quotes = [] }) {
  const claims = currentClaims();
  const email = (claims?.email || '').toLowerCase().trim();
  const [data, setData] = useState({ origen: 'vacio', equipos: [] });
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    listarEquiposMonitoreo({ email, quotes }).then((r) => {
      if (vivo) { setData(r || { origen: 'vacio', equipos: [] }); setCargando(false); }
    }).catch(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [email, quotes]);

  // 🔴 `no_disponible` NO puede pintarse como «no tienes equipos»: eso es una afirmación sobre
  // la cuenta del titular apoyada en una pregunta que no se pudo hacer. Va en ámbar y dice que
  // no significa que no haya flota.
  const noSePudo = data.origen === 'no_disponible';
  const nota = data.origen === 'fluxai'
    ? 'Fichas pedidas a FluxAI (recurso equipos). No se abre la consola AMI.'
    : data.origen === 'cotizacion'
      ? 'El puente de equipos no entregó flota. Se muestran los equipos ligados a tus cotizaciones.'
      : noSePudo
        ? `No se pudo consultar el puente de equipos${data.motivo ? ` (${data.motivo})` : ''}. Esto NO significa que no tengas equipos: la consulta no llegó a responderse. Vuelve a intentarlo en unos minutos.`
        : 'Aún no hay equipos asignados a este correo. FluxAI entrega la ficha cuando el medidor está asignado.';

  return (
    <div className="al-content sh-fade-up" style={{ maxWidth: 960, margin: '0 auto', padding: '16px 18px 36px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 7, display: 'grid', placeItems: 'center',
          color: C.teal, background: `${C.teal}14`, border: `1px solid ${C.teal}33`,
        }}>
          <ServiceIcon sku="monitoreo_premium" size={17} />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.heading }}>Monitoreo de planta</h1>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
            Solar Hub pide equipos y fichas. SCADA, reactiva e ISO viven en FluxAI.
          </div>
        </div>
      </div>

      {/* Ámbar SOLO cuando no se pudo preguntar: un aviso que se ve igual que el caso normal no
          avisa nada. Relleno suave y letra oscura — el color no se aplica a las dos cosas. */}
      <div style={{
        fontSize: 12, lineHeight: 1.5, marginBottom: 16, borderRadius: 8, padding: '10px 12px',
        color: !cargando && noSePudo ? '#7A4A00' : C.muted,
        background: !cargando && noSePudo ? `${C.amber}1A` : C.card,
        border: `1px solid ${!cargando && noSePudo ? `${C.amber}66` : C.border}`,
      }}>
        {cargando ? 'Consultando equipos…' : nota}
      </div>

      {!cargando && data.equipos.length === 0 && !noSePudo && (
        <div style={{ fontSize: 13, color: C.muted }}>No hay fichas para mostrar.</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
        {data.equipos.map((eq) => <Ficha key={eq.device_id} eq={eq} />)}
      </div>
    </div>
  );
}
